import { ReactElement, useState, useEffect, useMemo } from "react";
import { Box, Popover, IconButton, TextField, Typography, Button, Tooltip } from "@mui/material";
import { DataGrid, GridColDef, GridSortItem, GridEventListener } from "@mui/x-data-grid";
import "./LRSGrid.css";
import { Cell } from "./CrunchTimeReportInterfaces/Cell";
import { GuestChecksReportDialog } from "./ReportSelectionDialog";
import CommentIcon from "@mui/icons-material/Comment";

export interface CrunchTimeDate {
  date: string;
  sales: number;
  ctNetSales: number;
  netSales: number;
}

export interface CrunchTimeDateData {
  location: string;
  storeName: string;
  data: CrunchTimeDate[];
}

function CrunchTimeWeeklySalesDiffGrid({
  crunchTimeDatesData,
  coloredCellsChange,
  onColoredCellsChange,
  setAddedCellsParent,
  setRemovedCellsParent,
}: {
  crunchTimeDatesData: CrunchTimeDateData[] | undefined;
  coloredCellsChange: Cell[];
  onColoredCellsChange: (cells: Cell[]) => void;
  setAddedCellsParent: (cells: Cell[]) => void;
  setRemovedCellsParent: (cells: Cell[]) => void;
}): ReactElement {
  const [coloredCells, setColoredCells] = useState<Cell[]>([]);
  const [initialColoredCellsCopy, setInitialColoredCellsCopy] = useState<Cell[]>([]);
  const [addedCells, setAddedCells] = useState<Cell[]>([]);
  const [removedCells, setRemovedCells] = useState<Cell[]>([]);

  // Add state for color selector popover
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [currentCellInfo, setCurrentCellInfo] = useState<{ rowId: string; field: string } | null>(null);
  // Add state for the comment
  const [currentComment, setCurrentComment] = useState<string>("");

  const [showReportDialog, setShowReportDialog] = useState<boolean>(false);

  const [showOnlyCommentedCells, setShowOnlyCommentedCells] = useState(false);

  useEffect(() => {
    setColoredCells(coloredCellsChange);
    setInitialColoredCellsCopy(coloredCellsChange);
  }, []);

  useEffect(() => {
    setAddedCellsParent(addedCells);
    setRemovedCellsParent(removedCells);
  }, [addedCells, setAddedCellsParent, removedCells, setRemovedCellsParent]);

  const additionalDateColumns: GridColDef[] =
    crunchTimeDatesData?.[0]?.data
      .sort((a, b) => new Date(a.date).getDate() - new Date(b.date).getDate())
      .map((item) => ({
        field: item.date,
        headerName: item.date.split("T")[0],
        flex: 1,
        minWidth: 80,
        type: "number",
        renderCell: (params) => {
          const cellData = coloredCells.find((cell) => cell.RowId === params.id.toString() && cell.Field === params.field);
          const isColored = Boolean(cellData);
          const coloredCell = cellData?.Color;
          const cellColor = isColored ? (coloredCell === "G" ? "palegreen" : "palegoldenrod") : "transparent";
          const hasComment = Boolean(cellData?.Comment);

          const rowData = crunchTimeDatesData?.find((row) => row.location === params.id.toString());
          const cellDateData = rowData?.data.find((d) => d.date === params.field);

          const tooltipText = cellDateData
            ? `POS Total Sales: ${cellDateData.netSales}\nCrunch Time Total Sales: ${cellDateData.ctNetSales}`.trim()
            : "No data available";

          return (
            <Tooltip title={<pre style={{ margin: 0 }}>{tooltipText}</pre>} arrow>
              <div
                style={{
                  backgroundColor: cellColor,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  position: "relative",
                  cursor: "default",
                }}
              >
                {hasComment && (
                  <div
                    style={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "#f44336",
                    }}
                    title={cellData?.Comment}
                  />
                )}
                <span style={{ padding: "6px" }}>{params.value}</span>
              </div>
            </Tooltip>
          );
        },
      })) ?? [];

  const columns: GridColDef[] = [
    { field: "location", headerName: "Store Number", flex: 1, minWidth: 80 },
    { field: "storeName", headerName: "Store Name", flex: 1, minWidth: 150 },
    ...additionalDateColumns,
  ];

  const rows = crunchTimeDatesData?.map((item) => {
    const row: { location: string; storeName: string; [key: string]: any } = {
      location: item.location,
      storeName: item.storeName,
    };

    item.data.forEach((crunchTime) => {
      row[crunchTime.date.replace(/ /g, "")] = crunchTime.sales;
    });

    return row;
  });

  const filteredRows = useMemo(() => {
    if (!rows || !showOnlyCommentedCells) return rows;

    return rows.filter((row) => {
      return coloredCells.some((cell) => cell.RowId === row.location && cell.Comment && cell.Comment.trim() !== "");
    });
  }, [rows, showOnlyCommentedCells, coloredCells]);

  const [sortModel, setSortModel] = useState<GridSortItem[]>([{ field: "location", sort: "asc" }]);

  const handleCellClick: GridEventListener<"cellClick"> = (params, event) => {
    if (params.field === "location" || params.field === "storeName") return;

    // Store the current cell info and open the color selector
    setCurrentCellInfo({ rowId: params.id.toString(), field: params.field });

    // Load existing comment if there is one
    const existingCell = coloredCells.find((cell) => cell.RowId === params.id.toString() && cell.Field === params.field);
    setCurrentComment(existingCell?.Comment || "");

    setAnchorEl(event.currentTarget as HTMLElement);
  };

  const handleShowReport = () => {
    setShowReportDialog(true);
  };

  const handleReportSelected = (vendor: any) => {
    setShowReportDialog(false);
  };

  const handleReportDialogClose = () => {
    setShowReportDialog(false);
  };

  const handleColorSelect = (color: "Y" | "G") => {
    if (!currentCellInfo) return;

    const clickedCell: Cell = {
      RowId: currentCellInfo.rowId,
      Field: currentCellInfo.field,
      Color: color,
      Comment: currentComment.trim() || undefined,
    };

    setColoredCells((prev) => {
      const existingCellIndex = prev.findIndex((cell) => cell.RowId === clickedCell.RowId && cell.Field === clickedCell.Field);

      const existingCell = existingCellIndex !== -1 ? prev[existingCellIndex] : null;
      const wasInitiallyColored = initialColoredCellsCopy.some((cell) => cell.RowId === clickedCell.RowId && cell.Field === clickedCell.Field);
      const initialCell = initialColoredCellsCopy.find((cell) => cell.RowId === clickedCell.RowId && cell.Field === clickedCell.Field);

      // Define cases
      const cases = {
        REMOVE: () => {
          // Remove the cell
          const updatedCells = prev.filter((cell) => !(cell.RowId === clickedCell.RowId && cell.Field === clickedCell.Field));

          // Only add to removedCells if it was originally colored in the DB
          if (wasInitiallyColored) {
            setRemovedCells((prevRemoved) => [...prevRemoved, clickedCell]);
          }

          // Remove from addedCells if it was previously added
          setAddedCells((prevAdded) => prevAdded.filter((cell) => !(cell.RowId === clickedCell.RowId && cell.Field === clickedCell.Field)));

          return updatedCells;
        },
        UPDATE: () => {
          // Update existing cell
          const updatedCells = prev.map((cell) =>
            cell.RowId === clickedCell.RowId && cell.Field === clickedCell.Field ? { ...cell, Color: color, Comment: clickedCell.Comment } : cell
          );

          if (wasInitiallyColored) {
            const colorOrCommentChanged = initialCell?.Color !== color || initialCell?.Comment !== clickedCell.Comment;

            if (colorOrCommentChanged) {
              // Only track the original cell in removedCells if we're changing from initial state
              setRemovedCells((prevRemoved) => {
                const originalCellInRemoved = prevRemoved.some(
                  (cell) =>
                    cell.RowId === clickedCell.RowId &&
                    cell.Field === clickedCell.Field &&
                    cell.Color === initialCell?.Color &&
                    cell.Comment === initialCell?.Comment
                );

                if (!originalCellInRemoved) {
                  // Add the original cell state to removedCells
                  const originalCell = initialColoredCellsCopy.find((cell) => cell.RowId === clickedCell.RowId && cell.Field === clickedCell.Field);
                  if (originalCell) {
                    return [...prevRemoved, originalCell];
                  }
                }
                return prevRemoved;
              });

              // Add the updated cell to addedCells
              setAddedCells((prevAdded) => {
                const existsInAdded = prevAdded.some((cell) => cell.RowId === clickedCell.RowId && cell.Field === clickedCell.Field);
                return existsInAdded
                  ? prevAdded.map((cell) => (cell.RowId === clickedCell.RowId && cell.Field === clickedCell.Field ? clickedCell : cell))
                  : [...prevAdded, clickedCell];
              });
            } else {
              // Reverted to original state, remove from tracking
              setRemovedCells((prevRemoved) => prevRemoved.filter((cell) => !(cell.RowId === clickedCell.RowId && cell.Field === clickedCell.Field)));
              setAddedCells((prevAdded) => prevAdded.filter((cell) => !(cell.RowId === clickedCell.RowId && cell.Field === clickedCell.Field)));
            }
          } else {
            // For newly added cells, just update in addedCells
            setAddedCells((prevAdded) => {
              const existsInAdded = prevAdded.some((cell) => cell.RowId === clickedCell.RowId && cell.Field === clickedCell.Field);
              return existsInAdded
                ? prevAdded.map((cell) => (cell.RowId === clickedCell.RowId && cell.Field === clickedCell.Field ? clickedCell : cell))
                : [...prevAdded, clickedCell];
            });
          }

          return updatedCells;
        },
        ADD: () => {
          // Add new cell
          const updatedCells = [...prev, clickedCell];

          if (!wasInitiallyColored) {
            setAddedCells((prevAdded) => [...prevAdded, clickedCell]);
          }

          // Remove from removedCells if it was previously removed

          return updatedCells;
        },
      };

      // Determine which case to execute
      let actionType: "REMOVE" | "UPDATE" | "ADD";
      if (existingCell) {
        if (existingCell.Color === color && existingCell.Comment === clickedCell.Comment) {
          actionType = "REMOVE";
        } else {
          actionType = "UPDATE";
        }
      } else {
        actionType = "ADD";
      }
      const updatedCells = cases[actionType]();
      onColoredCellsChange(updatedCells);
      return updatedCells;
    });

    // Close the popover
    setAnchorEl(null);
  };

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentComment(event.target.value);
  };

  const handleSaveComment = () => {
    if (!currentCellInfo) return;

    const existingCell = coloredCells.find((cell) => cell.RowId === currentCellInfo.rowId && cell.Field === currentCellInfo.field);

    if (existingCell) {
      // Update the comment only, keep the existing color
      const updatedCell = {
        ...existingCell,
        Comment: currentComment.trim() || undefined,
      };

      setColoredCells((prev) =>
        prev.map((cell) => (cell.RowId === currentCellInfo.rowId && cell.Field === currentCellInfo.field ? updatedCell : cell))
      );

      // Handle tracking changes (similar to handleColorSelect logic)
      const wasInitiallyColored = initialColoredCellsCopy.some(
        (cell) => cell.RowId === currentCellInfo.rowId && cell.Field === currentCellInfo.field
      );
      const initialCell = initialColoredCellsCopy.find((cell) => cell.RowId === currentCellInfo.rowId && cell.Field === currentCellInfo.field);

      if (wasInitiallyColored) {
        if (initialCell?.Comment !== updatedCell.Comment) {
          setRemovedCells((prevRemoved) => {
            const isAlreadyRemoved = prevRemoved.some((cell) => cell.RowId === currentCellInfo.rowId && cell.Field === currentCellInfo.field);
            return isAlreadyRemoved
              ? prevRemoved.map((cell) => (cell.RowId === currentCellInfo.rowId && cell.Field === currentCellInfo.field ? updatedCell : cell))
              : [...prevRemoved, updatedCell];
          });
        } else {
          setRemovedCells((prevRemoved) =>
            prevRemoved.filter((cell) => !(cell.RowId === currentCellInfo.rowId && cell.Field === currentCellInfo.field))
          );
        }
      } else {
        setAddedCells((prevAdded) => {
          const existsInAdded = prevAdded.some((cell) => cell.RowId === currentCellInfo.rowId && cell.Field === currentCellInfo.field);
          return existsInAdded
            ? prevAdded.map((cell) => (cell.RowId === currentCellInfo.rowId && cell.Field === currentCellInfo.field ? updatedCell : cell))
            : [...prevAdded, updatedCell];
        });
      }

      onColoredCellsChange(
        coloredCells.map((cell) => (cell.RowId === currentCellInfo.rowId && cell.Field === currentCellInfo.field ? updatedCell : cell))
      );
    } else if (currentComment.trim()) {
      // Only add a new cell if there's actually a comment
      handleColorSelect("Y"); // Default to yellow if adding a comment to a cell without color
    }

    setAnchorEl(null);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setCurrentCellInfo(null);
    setCurrentComment("");
  };

  // Add a function to handle clearing a cell
  const handleClearCell = () => {
    if (!currentCellInfo) return;

    // Check if the cell exists in coloredCells
    const existingCellIndex = coloredCells.findIndex((cell) => cell.RowId === currentCellInfo.rowId && cell.Field === currentCellInfo.field);

    if (existingCellIndex === -1) {
      // Cell doesn't exist in coloredCells, nothing to clear
      setAnchorEl(null);
      return;
    }

    const existingCell = coloredCells[existingCellIndex];

    // Create a cell object for tracking removed cells
    const removedCell: Cell = {
      RowId: currentCellInfo.rowId,
      Field: currentCellInfo.field,
      Color: existingCell.Color,
      Comment: existingCell.Comment,
    };

    // Update coloredCells state by removing the cell
    setColoredCells((prev) => prev.filter((cell) => !(cell.RowId === currentCellInfo.rowId && cell.Field === currentCellInfo.field)));

    // Check if the cell was originally from DB
    const wasInitiallyColored = initialColoredCellsCopy.some((cell) => cell.RowId === currentCellInfo.rowId && cell.Field === currentCellInfo.field);

    if (wasInitiallyColored) {
      // Add to removedCells as it was from the DB and needs to be removed
      setRemovedCells((prev) => {
        if (prev.some((cell) => cell.RowId === currentCellInfo!.rowId && cell.Field === currentCellInfo!.field)) {
          return prev; // Already in removedCells
        }
        return [...prev, removedCell];
      });
    }

    // Remove from addedCells if it was previously added
    setAddedCells((prev) => prev.filter((cell) => !(cell.RowId === currentCellInfo!.rowId && cell.Field === currentCellInfo!.field)));

    // Update parent with new coloredCells state
    onColoredCellsChange(coloredCells.filter((cell) => !(cell.RowId === currentCellInfo.rowId && cell.Field === currentCellInfo.field)));

    // Close the popover
    setAnchorEl(null);
  };

  if (!crunchTimeDatesData || crunchTimeDatesData.length === 0) {
    return <></>;
  }

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1 }}>
        <Button
          variant={showOnlyCommentedCells ? "contained" : "outlined"}
          onClick={() => setShowOnlyCommentedCells(!showOnlyCommentedCells)}
          startIcon={<CommentIcon />}
          sx={{
            textTransform: "none",
            minWidth: "211px",
          }}
        >
          {showOnlyCommentedCells ? "Show All" : "Filter for Comments"}
        </Button>
      </Box>
      <DataGrid
        className="lrsCalendar"
        columns={columns}
        rows={filteredRows || []}
        getRowId={(row) => row.location}
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
        }}
        pageSizeOptions={[50, 100, 200]}
        sortModel={sortModel}
        onSortModelChange={(model) => setSortModel(model)}
        onCellClick={handleCellClick}
        disableRowSelectionOnClick
      />

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1, width: 340 }}>
          <Typography variant="subtitle2">Select color:</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton
              onClick={() => handleColorSelect("Y")}
              sx={{
                bgcolor: "palegoldenrod",
                width: 36,
                height: 36,
                "&:hover": { bgcolor: "gold" },
              }}
              aria-label="Yellow"
            />
            <IconButton
              onClick={() => handleColorSelect("G")}
              sx={{
                bgcolor: "palegreen",
                width: 36,
                height: 36,
                "&:hover": { bgcolor: "lightgreen" },
              }}
              aria-label="Green"
            />
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Add comment:
          </Typography>
          <TextField
            multiline
            rows={2}
            value={currentComment}
            onChange={handleCommentChange}
            placeholder="Add your comment here..."
            size="medium"
            fullWidth
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}>
            <Button variant="outlined" color="error" size="small" onClick={handleClearCell}>
              Clear
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleShowReport}
              sx={{
                minWidth: 90,
                height: 40,
                whiteSpace: "normal",
                lineHeight: 1.2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                padding: 0,
              }}
            >
              SHOW
              <br />
              DETAILS
            </Button>
            <Button variant="contained" size="small" onClick={handleSaveComment}>
              Ok
            </Button>
          </Box>
        </Box>
      </Popover>
      {showReportDialog && (
        <GuestChecksReportDialog
          storeNumber={currentCellInfo?.rowId}
          storeDate={currentCellInfo?.field}
          handleClose={handleReportDialogClose}
          handleReportSelected={handleReportSelected}
        />
      )}
    </Box>
  );
}

export default CrunchTimeWeeklySalesDiffGrid;
